import { Effect, Schema } from "effect";

import {
  ClusterName,
  ControllerName,
  NamespaceName,
} from "../k8s/ids";
import { AlertTypes } from "./types";

/**
 * Effect Schema for suppression reason
 */
export const SuppressionReasonSchema = Schema.Struct({
  reason: Schema.String.pipe(
    Schema.check(
      Schema.isMinLength(1, {
        message: "Suppression reason is required",
      })
    )
  ),
});

/**
 * Effect Schema for alert types (as literal union)
 */
export const AlertTypeSchema = Schema.Literals([
  AlertTypes.HighRestarts,
  AlertTypes.MissingLimits,
  AlertTypes.NodeResources,
  AlertTypes.Overprovisioning,
  AlertTypes.PodStatus,
]);

/**
 * Effect Schema for rule target selector
 */
export const VivusRuleTargetSchema = Schema.Struct({
  cluster: Schema.optional(ClusterName),
  controller: Schema.optional(ControllerName),
  namespace: Schema.optional(NamespaceName),
}).pipe(
  Schema.check(
    Schema.makeFilter((target) => {
      // At least one field must be specified
      const hasField =
        target.cluster !== undefined ||
        target.controller !== undefined ||
        target.namespace !== undefined;
      return hasField
        ? true
        : "Rule target must have at least one field (namespace, controller, or cluster)";
    })
  )
);

/**
 * Effect Schema for suppress object (partial record of alert type to reason)
 */
const SuppressSchema = Schema.Struct({
  highRestarts: Schema.optional(SuppressionReasonSchema),
  missingLimits: Schema.optional(SuppressionReasonSchema),
  nodeResources: Schema.optional(SuppressionReasonSchema),
  overprovisioning: Schema.optional(
    SuppressionReasonSchema
  ),
  podStatus: Schema.optional(SuppressionReasonSchema),
}).pipe(
  Schema.check(
    Schema.makeFilter((suppress) => {
      // At least one suppression must be specified
      const hasSuppress =
        suppress.highRestarts !== undefined ||
        suppress.missingLimits !== undefined ||
        suppress.nodeResources !== undefined ||
        suppress.overprovisioning !== undefined ||
        suppress.podStatus !== undefined;
      return hasSuppress
        ? true
        : "Rule must suppress at least one alert type";
    })
  )
);

/**
 * Effect Schema for suppression rule
 */
export const VivusRuleSchema = Schema.Struct({
  suppress: SuppressSchema,
  target: VivusRuleTargetSchema,
});

/**
 * Effect Schema for target declaration
 */
export const VivusTargetSchema = Schema.Struct({
  cluster: ClusterName.pipe(
    Schema.check(
      Schema.isMinLength(1, {
        message: "Cluster name is required",
      })
    )
  ),
  namespace: NamespaceName.pipe(
    Schema.check(
      Schema.isMinLength(1, {
        message: "Namespace is required",
      })
    )
  ),
});

/**
 * Effect Schema for repo mapping override
 */
export const RepoMappingSchema = Schema.Struct({
  namespace: NamespaceName.pipe(
    Schema.check(
      Schema.isMinLength(1, {
        message: "Namespace is required",
      })
    )
  ),
  workload: ControllerName.pipe(
    Schema.check(
      Schema.isMinLength(1, {
        message: "Workload name is required",
      })
    )
  ),
});

/**
 * Effect Schema for vivus.json configuration
 */
export const VivusConfigSchema = Schema.Struct({
  defaultAssignee: Schema.optional(Schema.String),
  repoMappings: Schema.optional(
    Schema.Array(RepoMappingSchema)
  ),
  rules: Schema.optional(Schema.Array(VivusRuleSchema)),
  targets: Schema.optional(Schema.Array(VivusTargetSchema)),
  version: Schema.Literal("1"),
});

/**
 * Inferred type from VivusConfigSchema
 */
export type VivusConfigParsed =
  typeof VivusConfigSchema.Type;

/**
 * Error type for vivus.json parsing failures
 */
export class VivusConfigParseError extends Schema.TaggedError<
  VivusConfigParseError,
  { readonly brand: unique symbol }
>()("VivusConfigParseError", {
  message: Schema.String,
  cause: Schema.optional(Schema.Defect()),
}) {}

/**
 * Parse and validate a vivus.json content string.
 * Returns Effect that fails with VivusConfigParseError if invalid.
 */
export const parseVivusConfig = Effect.fn(
  "VivusConfig.parse"
)(function* (content: string) {
  // Parse JSON first
  const json = yield* Effect.try({
    catch: (error) =>
      VivusConfigParseError.make({
        message: `Invalid JSON: ${error instanceof Error ? error.message : String(error)}`,
        cause: error,
      }),
    try: () => JSON.parse(content) as unknown,
  });

  // Validate against schema
  const config = yield* Schema.decodeUnknownEffect(
    VivusConfigSchema
  )(json).pipe(
    Effect.mapError((error) =>
      VivusConfigParseError.make({
        message: `Invalid vivus.json: ${error.message}`,
        cause: error,
      })
    )
  );

  return config;
});

/**
 * Generate JSON Schema from Effect Schema for IDE validation.
 * This is a static JSON Schema that matches VivusConfigSchema.
 */
export const generateJsonSchema = (): object => ({
  $id: "https://vivus.blogic.cz/schemas/vivus-config/v1/json",
  $schema: "http://json-schema.org/draft-07/schema#",
  additionalProperties: false,
  properties: {
    $schema: {
      description:
        "JSON Schema reference for IDE validation",
      type: "string",
    },
    defaultAssignee: {
      description:
        "Default GitHub username to assign issues to",
      type: "string",
    },
    repoMappings: {
      description:
        "Manual workload-to-repo mapping overrides. The repo containing this config is the target.",
      items: {
        additionalProperties: false,
        properties: {
          namespace: {
            description:
              "Namespace where the workload runs",
            minLength: 1,
            type: "string",
          },
          workload: {
            description:
              "Workload (Deployment/StatefulSet/DaemonSet) name to map to this repo",
            minLength: 1,
            type: "string",
          },
        },
        required: ["workload", "namespace"],
        type: "object",
      },
      type: "array",
    },
    rules: {
      description: "Suppression rules for alerts",
      items: {
        additionalProperties: false,
        properties: {
          suppress: {
            additionalProperties: false,
            description:
              "Alert types to suppress with reasons",
            minProperties: 1,
            properties: {
              highRestarts: {
                additionalProperties: false,
                properties: {
                  reason: {
                    description:
                      "Reason for suppressing this alert",
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["reason"],
                type: "object",
              },
              missingLimits: {
                additionalProperties: false,
                properties: {
                  reason: {
                    description:
                      "Reason for suppressing this alert",
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["reason"],
                type: "object",
              },
              nodeResources: {
                additionalProperties: false,
                properties: {
                  reason: {
                    description:
                      "Reason for suppressing this alert",
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["reason"],
                type: "object",
              },
              overprovisioning: {
                additionalProperties: false,
                properties: {
                  reason: {
                    description:
                      "Reason for suppressing this alert",
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["reason"],
                type: "object",
              },
              podStatus: {
                additionalProperties: false,
                properties: {
                  reason: {
                    description:
                      "Reason for suppressing this alert",
                    minLength: 1,
                    type: "string",
                  },
                },
                required: ["reason"],
                type: "object",
              },
            },
            type: "object",
          },
          target: {
            additionalProperties: false,
            description:
              "Target selector - at least one field required",
            minProperties: 1,
            properties: {
              cluster: {
                description:
                  "Cluster name to match (exact)",
                type: "string",
              },
              controller: {
                description:
                  "Controller name to match (exact)",
                type: "string",
              },
              namespace: {
                description: "Namespace to match (exact)",
                type: "string",
              },
            },
            type: "object",
          },
        },
        required: ["target", "suppress"],
        type: "object",
      },
      type: "array",
    },
    targets: {
      description: "Target namespace/cluster declarations",
      items: {
        additionalProperties: false,
        properties: {
          cluster: {
            description: "Cluster name where workloads run",
            minLength: 1,
            type: "string",
          },
          namespace: {
            description: "Namespace where workloads run",
            minLength: 1,
            type: "string",
          },
        },
        required: ["namespace", "cluster"],
        type: "object",
      },
      type: "array",
    },
    version: {
      const: "1",
      description: "Config version (currently only '1')",
    },
  },
  required: ["version"],
  title: "Vivus Configuration",
  type: "object",
});
